import { useState } from 'react';
import { T } from 'gt-react';
import type { APInvoice } from '@erp/rpc';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useLinkArCollectionThreadApInvoiceEvidence } from '@/api/rpc/hooks/accounts-receivable/useArCollectionThreads';
import { ApInvoicePickerCombobox } from './ApInvoicePickerCombobox';

interface Props {
  threadId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Modal to manually link an AP (vendor/carrier) invoice as evidence on the
 * thread. Useful when the automatic gather missed a freight invoice that's tied
 * to the order only by a shared reference number. Links the invoice plus its
 * supporting documents (BOL, POD, carrier confirmation).
 */
export function LinkApInvoiceDialog({ threadId, open, onOpenChange }: Props) {
  const [selected, setSelected] = useState<APInvoice | null>(null);
  const link = useLinkArCollectionThreadApInvoiceEvidence();

  function handleOpenChange(next: boolean) {
    onOpenChange(next);
    if (!next) {
      setSelected(null);
      link.reset();
    }
  }

  function handleLink() {
    if (!selected) return;
    // mutate (not mutateAsync) so a server-side failure doesn't bubble up as an
    // unhandled promise rejection from this onClick handler — the error is
    // surfaced inline via link.isError below.
    link.mutate(
      { id: threadId, apInvoiceId: selected.id },
      { onSuccess: () => handleOpenChange(false) },
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            <T context="dialog title, link an AP invoice as thread evidence">
              Link AP invoice
            </T>
          </DialogTitle>
          <DialogDescription>
            <T context="dialog help, what linking an AP invoice does">
              Search for a vendor or carrier invoice to link as evidence. Its
              supporting documents (BOL, POD, carrier confirmation) are linked
              too.
            </T>
          </DialogDescription>
        </DialogHeader>
        <ApInvoicePickerCombobox
          value={selected}
          onChange={setSelected}
          disabled={link.isPending}
        />
        {link.isError && (
          <p className="text-sm text-destructive">
            <T context="error, failed to link AP invoice">
              Could not link this invoice. Please try again.
            </T>
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={link.isPending}
          >
            <T context="button, cancel linking AP invoice">Cancel</T>
          </Button>
          <Button onClick={handleLink} disabled={!selected || link.isPending}>
            <T context="button, confirm link AP invoice">Link invoice</T>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
